import React, { useEffect, useState } from 'react';
import styles from './InfoTooltip.module.scss';
import { useTranslation } from 'react-i18next';
import { IndicatorKeyEnum } from '../../../models/IndicatorKeyEnum';
import { useSelector } from 'react-redux';
import { ReactComponent as IconInfo } from '../../../assets/icons/u_info-circle.svg';
import { ReactComponent as IconClose } from '../../../assets/icons/u_multiply.svg';

type Props = {
  indicatorKey?: string;
  title?: string;
  body?: string;
  children?: React.ReactNode;
  openOnHover?: boolean;
};

const displayUrl = (indicatorKey?: string) => {
  const url = 'https://mici.iadb.org/es/cases';

  return indicatorKey === IndicatorKeyEnum.PROJECTS_IN_EXECUTION_WITH_MICI_OPEN_CASES ? (
    <a href={url} className={styles.link} target="_blank" rel="noopener noreferrer">
      {url}
    </a>
  ) : null;
};

const getIndicatorTooltip = (allIndicators, indicatorKey) => {
  const filterTabIndicators = allIndicators.find(i => i.indicators.some(indicator => indicator.code === indicatorKey));
  return filterTabIndicators?.indicators.find(indicator => indicator.code === indicatorKey);
};

const InfoTooltip = ({ indicatorKey, title, body, children, openOnHover = false }: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [indicatorInfo, setIndicatorInfo] = useState<any>();
  const allIndicatorsFromState = useSelector<any, any[]>(state => state.allIndicators);


  const language = localStorage.getItem('language') || 'en';



  const handleClickOutside = event => {
    if (event.target.className !== styles.modalInfo) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (indicatorKey) {
      setIndicatorInfo(getIndicatorTooltip(allIndicatorsFromState, indicatorKey));
    }

    if (isOpen) {
      window.addEventListener('click', handleClickOutside);
    } else {
      window.removeEventListener('click', handleClickOutside);
    }
    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  const { t } = useTranslation();


  const splitTooltipText = indicatorInfo?.tooltip?.split('|');

  return (
    <div
      className={`${styles.container} ${openOnHover ? styles.hoverContainer : ''}`}
      onMouseEnter={openOnHover ? () => setIsOpen(true) : undefined}
      onMouseLeave={openOnHover ? () => setIsOpen(false) : undefined}
      onFocus={openOnHover ? () => setIsOpen(true) : undefined}
      onBlur={openOnHover ? event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) setIsOpen(false);
      } : undefined}
      onKeyDown={openOnHover ? event => {
        if (event.key === 'Escape') setIsOpen(false);
      } : undefined}
    >
      <div
        className={styles.clickable}
        tabIndex={openOnHover ? 0 : undefined}
        onClick={event => {
          event.stopPropagation();
          setIsOpen(!isOpen);
        }}
      >
        {children || <IconInfo />}
      </div>
      {isOpen ? (
        <div className={styles.modalInfo} onClick={event => event.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h4>{title || t('about_this_indicator')}</h4>
            <div className={styles.iconClose} onClick={() => setIsOpen(false)}>
              <IconClose />
            </div>
          </div>
          {body !== undefined ? <p>{body}</p> : indicatorKey === IndicatorKeyEnum.CLASSIFICATION_OF_ACQUISITION_PLANS ? (
            <>
              <p>{splitTooltipText[0]}</p>
              <p>{splitTooltipText[1]}</p>

            </>
          ) : indicatorKey === IndicatorKeyEnum.PCR_NEXT_PRESENTATION ? (
            <>
              <p>{splitTooltipText[0]}</p>
              <p>{splitTooltipText[1]}</p>
              <a href={splitTooltipText[2]} className={styles.link} target="_blank" rel="noopener noreferrer">
                {t('link_for_more_information')}
              </a>
            </>
          ) : (
            <>
              <p>{indicatorInfo && indicatorInfo?.tooltip ? indicatorInfo.tooltip : ''}</p>
              {displayUrl(indicatorKey)}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default InfoTooltip;
